'use client'

import { useEffect } from 'react'
import {
  ATTENDANCE_REMINDERS,
  reminderStorageKey,
} from '@/lib/attendance-reminders'

// Fire a reminder up to this long after its scheduled time, so a user who opens
// the app a little after 9:30 / 18:30 still gets the nudge (but not at midnight).
const GRACE_MS = 2 * 60 * 60 * 1000
// How often we re-check the clock. 60s is well within the 1-minute precision
// we need for reminders scheduled on the half hour.
const CHECK_INTERVAL_MS = 60 * 1000

/**
 * Schedules browser notifications for attendance actions (check-in at 9:30 AM,
 * check-out at 6:30 PM). No-ops unless the user has granted notification
 * permission. Each reminder fires at most once per calendar day.
 */
export function useAttendanceReminders() {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return

    const tick = () => {
      if (Notification.permission !== 'granted') return
      const now = new Date()

      for (const reminder of ATTENDANCE_REMINDERS) {
        const target = new Date(now)
        target.setHours(reminder.hour, reminder.minute, 0, 0)

        const elapsed = now.getTime() - target.getTime()
        if (elapsed < 0 || elapsed > GRACE_MS) continue

        const key = reminderStorageKey(reminder.slot, now)
        if (localStorage.getItem(key)) continue

        try {
          new Notification(reminder.title, {
            body: reminder.body,
            icon: '/icons/icon-192.png',
            tag: key,
          })
          localStorage.setItem(key, '1')
        } catch {
          // Notification construction can throw on some platforms — ignore and retry next tick.
        }
      }
    }

    tick()
    const id = window.setInterval(tick, CHECK_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [])
}
